import * as React from 'react';
import type { JSX } from 'react';
import type { BreadcrumbItem } from './Header';

interface BreadcrumbsContextValue {
  breadcrumbs: BreadcrumbItem[];
  baseBreadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
}

const BreadcrumbsContext = React.createContext<BreadcrumbsContextValue | undefined>(undefined);

export function BreadcrumbsProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: BreadcrumbsContextValue;
}): JSX.Element {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (typeof window === 'undefined') return;
    if (!value.baseBreadcrumbs.length) return;
    if (value.breadcrumbs.length > 0) return;

    const timer = window.setTimeout(() => {
      console.warn(
        '[breadcrumbs] Screen did not publish a breadcrumb trail beyond the base items.'
      );
    }, 0);

    return () => window.clearTimeout(timer);
  }, [value.breadcrumbs, value.baseBreadcrumbs]);

  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
}

function useBreadcrumbsContext(): BreadcrumbsContextValue {
  const context = React.useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbsContext must be used within a BreadcrumbsProvider');
  }
  return context;
}

export function useBreadcrumbSetter(): (items: BreadcrumbItem[]) => void {
  const { setBreadcrumbs } = useBreadcrumbsContext();
  return setBreadcrumbs;
}

export function useBaseBreadcrumbs(): BreadcrumbItem[] {
  const { baseBreadcrumbs } = useBreadcrumbsContext();
  return baseBreadcrumbs;
}

export function useCurrentBreadcrumbs(): BreadcrumbItem[] {
  const { breadcrumbs } = useBreadcrumbsContext();
  return breadcrumbs;
}

export function useBreadcrumbEffect(factory: (base: BreadcrumbItem[]) => BreadcrumbItem[]): void {
  const baseBreadcrumbs = useBaseBreadcrumbs();
  const setBreadcrumbs = useBreadcrumbSetter();

  React.useEffect(() => {
    const next = factory([...baseBreadcrumbs]);
    setBreadcrumbs(next);
  }, [baseBreadcrumbs, factory, setBreadcrumbs]);
}
